import React, { useEffect, useMemo, useState } from 'react';
import { ShoppingCart, Scale, ChefHat, Leaf, Star, Wine, AlertCircle, Maximize2 } from 'lucide-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { formatPrice, formatPricePerKg } from '@/lib/formatPrice';
import { resolveImageUrl } from '@/lib/utils';
import { APP_BASE } from '@/lib/api';
import { DishImagePreviewDialog } from '@/components/DishImagePreviewDialog';
import { buildCategoryGroups, HOME_CATEGORY_GROUPS } from '@/lib/menuCategoryGroups';

const badgeStyles = {
  consigliato: 'gradient-instagram text-primary-foreground border-0',
  vegetariano: 'bg-success/20 text-success border-success/30',
  'senza-glutine': 'bg-warning/20 text-warning-foreground border-warning-foreground/30',
  novita: 'bg-accent/20 text-accent-foreground border-accent/30'
};

const badgeLabels = {
  consigliato: 'Consigliato',
  vegetariano: 'Vegetariano',
  'senza-glutine': 'Senza Glutine',
  novita: 'Novita'
};

const scrollToBevande = () => {
  const el = document.getElementById('bevande');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const TopNavigationButtons = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
  };

  const handleGoBevande = () => {
    scrollToBevande();
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
      <Button
        type="button"
        variant="outline"
        className="h-12 px-6 rounded-2xl text-base font-semibold"
        onClick={handleGoHome}
      >
        Torna alla home
      </Button>

      <Button
        type="button"
        className="h-12 px-6 rounded-2xl text-base font-semibold gradient-instagram text-primary-foreground hover:opacity-90"
        onClick={handleGoBevande}
      >
        Vai alle bevande
      </Button>
    </div>
  );
};

const FullMenuDishCard = ({ dish, categoryName }) => {
  const [grams, setGrams] = useState(300);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { addItem } = useCart();

  const canShowPrice =
    dish.show_price !== false &&
    dish.price_value != null;

  const price300g = dish.price_value;
  const pricePerKg = price300g ? (price300g / 0.3) : null;
  const isFixedPrice = dish.price_type === 'fixed';

  const calculatedPrice = isFixedPrice
    ? price300g
    : (pricePerKg ? (grams / 1000) * pricePerKg : null);

  const handleAddToCart = () => {
    if (calculatedPrice == null) return;

    const dishWithPrice = {
      ...dish,
      category: categoryName,
      price300g,
      pricePerKg,
      calculatedPrice,
      isFixedPrice
    };

    if (isFixedPrice) {
      addItem(dishWithPrice, 1, true);
    } else {
      addItem(dishWithPrice, grams);
    }

    const priceLabel = isFixedPrice
      ? formatPrice(calculatedPrice)
      : `${grams}g - ${formatPrice(calculatedPrice)}`;

    toast.success(`${dish.name} (${priceLabel}) aggiunto al carrello!`, {
      duration: 2000,
    });
  };

  const categoryColors = {
    'primi di terra': 'from-instagram-red/10 to-instagram-orange/10',
    'primi di mare': 'from-sky-500/10 to-cyan-500/10',
    'secondi di terra': 'from-instagram-orange/10 to-instagram-yellow/5',
    'secondi di mare': 'from-blue-500/10 to-sky-400/10',
    'contorni': 'from-success/10 to-success/5',
    'antipasti': 'from-instagram-purple/10 to-instagram-red/5',
    'dolci': 'from-pink-500/10 to-pink-300/5',
    'bevande': 'from-cyan-500/10 to-blue-500/10'
  };

  const normalizedCategory = categoryName?.toLowerCase() || 'altro';
  const bgColor = categoryColors[normalizedCategory] || 'from-muted/30 to-muted/10';
  const hasImage = !!dish.image_url;
  const imageUrl = resolveImageUrl(dish.image_url);
  const fixedHeightClass = isFixedPrice ? 'min-h-[390px] sm:min-h-[430px] md:min-h-[450px]' : '';
  const canPreviewImage = hasImage && !!imageUrl;
  const canPreviewFromCard =
    canPreviewImage &&
    !(typeof window !== 'undefined' && window.matchMedia('(hover: none), (pointer: coarse)').matches);
  const openPreview = () => setIsPreviewOpen(true);

  const handlePreviewOpen = (event) => {
    if (!canPreviewFromCard) return;

    const interactiveElement = event.target.closest(
      'button, a, input, textarea, select, label, [role="button"], [role="slider"], [data-no-preview="true"]'
    );

    if (interactiveElement && interactiveElement !== event.currentTarget) return;
    openPreview();
  };

  const handlePreviewKeyDown = (event) => {
    if (!canPreviewImage || event.target !== event.currentTarget) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPreview();
    }
  };

  return (
    <>
      <Card
        className={`relative overflow-hidden rounded-xl border border-border/50 hover:border-instagram-purple/30 hover:shadow-medium transition-all duration-300 ${
          !hasImage ? `bg-gradient-to-br ${bgColor}` : ''
        } h-full ${fixedHeightClass} ${canPreviewFromCard ? 'cursor-zoom-in' : ''}`}
        onClick={handlePreviewOpen}
        onKeyDown={handlePreviewKeyDown}
        role={canPreviewFromCard ? 'button' : undefined}
        tabIndex={canPreviewFromCard ? 0 : undefined}
        aria-label={canPreviewFromCard ? `Apri immagine di ${dish.name}` : undefined}
      >
      {hasImage && imageUrl && (
        <>
          <img
            src={imageUrl}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="lazy"
            onError={(e) => {
              console.error('[FullMenu] Image load failed:', imageUrl);
              e.target.style.display = 'none';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
        </>
      )}

      {canPreviewImage && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            openPreview();
          }}
          className="absolute right-3 top-3 z-20 inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/45 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white backdrop-blur-md transition hover:bg-black/60"
          aria-label={`Ingrandisci immagine di ${dish.name}`}
        >
          <Maximize2 className="h-3.5 w-3.5" />
          <span>Ingrandisci</span>
        </button>
      )}

      <div className={`relative z-10 flex h-full flex-col gap-4 p-4 sm:p-5 ${hasImage ? 'text-white' : ''}`}>
        <div className="flex-1">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className={`font-semibold text-base sm:text-lg leading-tight ${hasImage ? 'text-white drop-shadow-md' : 'text-foreground'}`}>
              {dish.name}
            </h3>

            {dish.badges && dish.badges.includes('consigliato') && (
              <Star
                className={`w-5 h-5 flex-shrink-0 ${hasImage ? 'text-yellow-400' : 'text-instagram-orange'}`}
                fill="currentColor"
              />
            )}
          </div>

          {dish.description ? (
            <p className={`text-sm line-clamp-2 ${hasImage ? 'text-white/90 drop-shadow' : 'text-muted-foreground'}`}>
              {dish.description}
            </p>
          ) : null}

          {dish.badges && dish.badges.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {dish.badges.map((badge) => (
                <Badge
                  key={badge}
                  variant="outline"
                  className={`text-[10px] px-2 py-0.5 ${
                    hasImage
                      ? 'bg-white/20 text-white border-white/30 backdrop-blur-sm'
                      : badgeStyles[badge] || ''
                  }`}
                >
                  {badgeLabels[badge] || badge}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {canShowPrice && price300g != null ? (
          <div className={`space-y-3 pt-2 border-t ${hasImage ? 'border-white/20' : 'border-border/50'}`}>
            {isFixedPrice ? (
              <>
                <div className="flex justify-between items-center">
                  <span className={hasImage ? 'text-white/80' : 'text-muted-foreground'}>Prezzo:</span>
                  <span className={`text-2xl font-bold ${hasImage ? 'text-white drop-shadow-lg' : 'gradient-instagram-text'}`}>
                    {formatPrice(price300g)}
                  </span>
                </div>

                <Button
                  onClick={handleAddToCart}
                  className={`w-full ${hasImage ? 'bg-white text-gray-900 hover:bg-white/90' : 'gradient-instagram text-primary-foreground hover:opacity-90'} shadow-instagram`}
                  data-testid={`full-menu-add-to-cart-${dish.id}`}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Aggiungi al Carrello
                </Button>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center text-sm">
                  <span className={hasImage ? 'text-white/80' : 'text-muted-foreground'}>Prezzo 300g:</span>
                  <span className={`font-medium ${hasImage ? 'text-white' : 'text-foreground'}`}>
                    {formatPrice(price300g)}
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <span className={hasImage ? 'text-white/80' : 'text-muted-foreground'}>Prezzo al kg:</span>
                  <span className={`font-medium ${hasImage ? 'text-white' : 'text-foreground'}`}>
                    {formatPricePerKg(pricePerKg)}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Scale className={`w-4 h-4 ${hasImage ? 'text-white' : 'text-instagram-purple'}`} />
                      <span className={`text-sm font-medium ${hasImage ? 'text-white' : 'text-foreground'}`}>
                        Quantita:
                      </span>
                    </div>
                    <span className={`text-sm font-bold ${hasImage ? 'text-white' : 'gradient-instagram-text'}`}>
                      {grams}g
                    </span>
                  </div>

                  <Slider
                    value={[grams]}
                    onValueChange={(value) => setGrams(value[0])}
                    min={100}
                    max={1000}
                    step={50}
                    className="w-full"
                  />

                  <div className={`flex justify-between text-xs ${hasImage ? 'text-white/70' : 'text-muted-foreground'}`}>
                    <span>100g</span>
                    <span>500g</span>
                    <span>1kg</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div>
                    <span className={`text-xs ${hasImage ? 'text-white/80' : 'text-muted-foreground'}`}>
                      Totale:
                    </span>
                    <p className={`text-2xl font-bold ${hasImage ? 'text-white drop-shadow-lg' : 'gradient-instagram-text'}`}>
                      {formatPrice(calculatedPrice)}
                    </p>
                  </div>

                  <Button
                    onClick={handleAddToCart}
                    className={`${hasImage ? 'bg-white text-gray-900 hover:bg-white/90' : 'gradient-instagram text-primary-foreground hover:opacity-90'} shadow-instagram`}
                    data-testid={`full-menu-add-to-cart-${dish.id}`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Aggiungi
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className={`pt-2 border-t ${hasImage ? 'border-white/20' : 'border-border/50'}`}>
            <p className={`text-sm italic ${hasImage ? 'text-white/70' : 'text-muted-foreground'}`}>
              Prezzo non disponibile
            </p>
            <Button disabled variant="outline" className="mt-2 w-full">
              Non ordinabile
            </Button>
          </div>
        )}
      </div>
      </Card>

      <DishImagePreviewDialog
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        imageUrl={imageUrl}
        title={dish.name}
        categoryName={categoryName}
      />
    </>
  );
};

export const FullMenuSection = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedCategoryKey = (searchParams.get('categoria') || '').toLowerCase();

  useEffect(() => {
    if (location.hash === '#bevande') {
      const timer = setTimeout(() => {
        scrollToBevande();
      }, 250);

      return () => clearTimeout(timer);
    }

    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const fetchFullMenu = async () => {
      try {
        const response = await fetch(`${APP_BASE}/api/public/menu/full/index.php`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || data?.error || 'Errore nel caricamento del menu completo');
        }

        setCategories(Array.isArray(data?.categories) ? data.categories : []);
        setError(null);
      } catch (err) {
        console.error('Error fetching full menu:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFullMenu();
  }, []);

  useEffect(() => {
    if (!loading && window.location.hash === '#bevande') {
      const timer = setTimeout(() => {
        scrollToBevande();
      }, 250);

      return () => clearTimeout(timer);
    }
  }, [loading, categories]);

  const categoryIcons = {
    'primi di terra': { icon: ChefHat, color: 'text-instagram-red' },
    'primi di mare': { icon: ChefHat, color: 'text-sky-500' },
    'secondi di terra': { icon: ChefHat, color: 'text-instagram-orange' },
    'secondi di mare': { icon: ChefHat, color: 'text-blue-500' },
    'contorni': { icon: Leaf, color: 'text-success' },
    'antipasti': { icon: ChefHat, color: 'text-instagram-purple' },
    'dolci': { icon: ChefHat, color: 'text-pink-500' },
    'bevande': { icon: Wine, color: 'text-cyan-600' }
  };

  const foodCategories = useMemo(() => {
    return categories.filter((category) => {
      const slug = (category.slug || '').toLowerCase();
      const name = (category.name || '').toLowerCase();
      return slug !== 'bevande' && name !== 'bevande';
    });
  }, [categories]);

  const beverageCategories = useMemo(() => {
    return categories.filter((category) => {
      const slug = (category.slug || '').toLowerCase();
      const name = (category.name || '').toLowerCase();
      return slug === 'bevande' || name === 'bevande';
    });
  }, [categories]);

  const groupedFoodCategories = useMemo(
    () => buildCategoryGroups(foodCategories, ['antipasti', 'primi', 'secondi', 'contorni']),
    [foodCategories]
  );

  const selectedCategoryGroup = useMemo(
    () => HOME_CATEGORY_GROUPS.find((group) => group.key === selectedCategoryKey) || null,
    [selectedCategoryKey]
  );

  const filteredCategoryGroup = useMemo(() => {
    if (!selectedCategoryGroup) return null;
    return groupedFoodCategories.find((group) => group.key === selectedCategoryGroup.key) || null;
  }, [groupedFoodCategories, selectedCategoryGroup]);

  if (loading) {
    return (
      <section className="pt-32 pb-10 px-4 bg-muted/20 scroll-mt-24" id="menu-completo">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-instagram-purple"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="pt-32 pb-10 px-4 bg-muted/20 scroll-mt-24" id="menu-completo">
        <div className="max-w-5xl mx-auto text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-3">Menu completo non disponibile</h2>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </section>
    );
  }

  if (categories.length === 0) {
    return (
      <section className="pt-32 pb-10 px-4 bg-muted/20 scroll-mt-24" id="menu-completo">
        <div className="max-w-5xl mx-auto text-center">
          <TopNavigationButtons />
          <Card className="border-dashed">
            <div className="p-6 text-muted-foreground">
              Nessun elemento disponibile nel menu completo al momento.
            </div>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="pt-32 pb-10 px-4 bg-muted/20 scroll-mt-24" id="menu-completo">
        <div className="max-w-5xl mx-auto">
          <TopNavigationButtons />

          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full gradient-instagram animate-pulse" />
              <span className="text-xs font-semibold uppercase tracking-wider text-instagram-purple">
                Menu Completo
              </span>
              <div className="w-2 h-2 rounded-full gradient-instagram animate-pulse" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              {selectedCategoryGroup ? (
                <>
                  Tutti i nostri <span className="gradient-instagram-text">{selectedCategoryGroup.label.toLowerCase()}</span>
                </>
              ) : (
                <>
                  Scopri il nostro <span className="gradient-instagram-text">menu completo</span>
                </>
              )}
            </h2>

            <p className="text-sm text-muted-foreground">
              {selectedCategoryGroup
                ? `Qui trovi tutti gli elementi disponibili nella categoria ${selectedCategoryGroup.label.toLowerCase()}.`
                : 'Piatti disponibili oggi nel nostro menu completo'}
            </p>

            {selectedCategoryGroup ? (
              <div className="mt-5 flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-2xl"
                  onClick={() => navigate('/menu-completo')}
                >
                  Torna a tutte le categorie
                </Button>
              </div>
            ) : null}
          </div>

          <div className="space-y-8">
            {selectedCategoryGroup ? (
              filteredCategoryGroup && filteredCategoryGroup.items.length > 0 ? (
                <div key={filteredCategoryGroup.key}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-card border border-border text-instagram-purple">
                      {filteredCategoryGroup.key === 'contorni' ? (
                        <Leaf className="w-5 h-5" />
                      ) : (
                        <ChefHat className="w-5 h-5" />
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-foreground">
                      {filteredCategoryGroup.label}
                    </h3>
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  <div className="grid grid-cols-1 items-stretch md:grid-cols-2 gap-4">
                    {filteredCategoryGroup.items.map((dish, index) => (
                      <div
                        key={`${filteredCategoryGroup.key}-${dish.id}-${index}`}
                        className="animate-slide-up h-full"
                        style={{ animationDelay: `${index * 0.08}s` }}
                      >
                        <FullMenuDishCard
                          dish={dish}
                          categoryName={dish.sourceCategoryName || filteredCategoryGroup.label}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Card className="border-dashed">
                  <div className="p-6 text-center text-muted-foreground">
                    Nessun elemento disponibile in {selectedCategoryGroup.label.toLowerCase()} al momento.
                  </div>
                </Card>
              )
            ) : foodCategories.map((category) => {
              const items = Array.isArray(category.items) ? category.items : [];
              if (items.length === 0) return null;

              const normalizedCategoryName = (category.name || '').toLowerCase();
              const categoryInfo =
                categoryIcons[normalizedCategoryName] || {
                  icon: ChefHat,
                  color: 'text-muted-foreground'
                };
              const IconComponent = categoryInfo.icon;

              return (
                <div key={category.id}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-lg bg-card border border-border ${categoryInfo.color}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground">
                      {category.name}
                    </h3>
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  <div className="grid grid-cols-1 items-stretch md:grid-cols-2 gap-4">
                    {items.map((dish, index) => (
                      <div
                        key={dish.id}
                        className="animate-slide-up h-full"
                        style={{ animationDelay: `${index * 0.08}s` }}
                      >
                        <FullMenuDishCard
                          dish={dish}
                          categoryName={category.name}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {!selectedCategoryGroup && foodCategories.length === 0 && (
              <Card className="border-dashed">
                <div className="p-6 text-center text-muted-foreground">
                  Nessun piatto disponibile al momento
                </div>
              </Card>
            )}
          </div>
        </div>
      </section>

      {!selectedCategoryGroup ? (
        <section className="py-10 px-4 scroll-mt-24" id="bevande">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />
                <span className="text-xs font-semibold uppercase tracking-wider text-cyan-600">
                  Bevande
                </span>
                <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
                Aggiungi le tue <span className="text-cyan-600">bevande</span>
              </h2>

              <p className="text-sm text-muted-foreground">
                Bibite, acqua, vino, birra e altro
              </p>
            </div>

            <div className="space-y-8">
              {beverageCategories.map((category) => {
                const items = Array.isArray(category.items) ? category.items : [];
                if (items.length === 0) return null;

                return (
                  <div key={category.id}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-card border border-border text-cyan-600">
                        <Wine className="w-5 h-5" />
                      </div>
                      <h3 className="text-lg font-bold text-foreground">
                        {category.name}
                      </h3>
                      <div className="flex-1 h-px bg-border" />
                    </div>

                    <div className="grid grid-cols-1 items-stretch md:grid-cols-2 gap-4">
                      {items.map((dish, index) => (
                        <div
                          key={dish.id}
                          className="animate-slide-up h-full"
                          style={{ animationDelay: `${index * 0.08}s` }}
                        >
                          <FullMenuDishCard
                            dish={dish}
                            categoryName={category.name}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {beverageCategories.length === 0 && (
                <Card className="border-dashed">
                  <div className="p-6 text-center text-muted-foreground">
                    Nessuna bevanda disponibile al momento
                  </div>
                </Card>
              )}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
};
